import React from "react";
import { useDispatch, useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import moment from "moment";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import { getAllLocationReceived } from "../actions/locationReceived";
import { formatNumber } from "../Utils/format";

const DASHBOARD = "/stock/";
const SHOWN = 6;

// Tells a branch user about items head office has issued to them that they
// still have to confirm (they only join the branch's stock, and its reports,
// once confirmed). Checks when the app opens and every time the user comes
// to the dashboard, with a fresh list each time.
export default function PendingIssuesPrompt() {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [pending, setPending] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const firstCheck = React.useRef(true);

  const user = JSON.parse(secureLocalStorage.getItem("profile") || "null");
  const right = user?.results?.stock;
  const branch = user?.results?.branch || "";
  // head office and approvers don't receive issues
  const receivesIssues = !!user && right !== "Administrator" && right !== "Approver";
  const confirmPage = `/stock/${branch}/new-items`;

  React.useEffect(() => {
    if (!receivesIssues) return undefined;
    const onDashboard = pathname === DASHBOARD;
    const isFirst = firstCheck.current;
    firstCheck.current = false;
    // on opening the app (unless straight onto the confirm page), and on the dashboard
    if (!(onDashboard || (isFirst && pathname !== confirmPage))) return undefined;

    let current = true;
    Promise.resolve(dispatch(getAllLocationReceived())).then(() => {
      if (!current) return;
      const list = store.getState().received;
      const rows = (Array.isArray(list) ? list : []).slice().sort((a, b) => new Date(a.date) - new Date(b.date));
      setPending(rows);
      setOpen(rows.length > 0);
    });
    return () => {
      current = false;
    };
  }, [pathname, receivesIssues, confirmPage, dispatch, store]);

  if (!receivesIssues) return null;

  const review = () => {
    setOpen(false);
    navigate(confirmPage);
  };
  const count = pending.length;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "rgba(18,124,67,0.12)",
            color: "#127C43",
            flexShrink: 0,
          }}
        >
          <MoveToInboxIcon />
        </Box>
        <Box>
          <Typography component="span" sx={{ fontWeight: 900, fontSize: 18, display: "block" }}>
            {count} item{count === 1 ? "" : "s"} waiting for your confirmation
          </Typography>
          <Typography component="span" variant="body2" color="text.secondary">
            Head office has issued these to your branch. They become part of your stock only once you confirm them.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack divider={<Divider flexItem />} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          {pending.slice(0, SHOWN).map((row) => (
            <Box
              key={row._id}
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 1.5, py: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{row.item}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Issued {moment(row.date).format("DD MMM YYYY")}
                  {row.createdBy ? ` by ${row.createdBy}` : ""}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{formatNumber(row.quantity)}</Typography>
            </Box>
          ))}
        </Stack>
        {count > SHOWN && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            and {count - SHOWN} more
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>
          Later
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={review}
          sx={{ textTransform: "none", fontWeight: 800, bgcolor: "#127C43", "&:hover": { bgcolor: "#0f6a39" } }}
        >
          Review and confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
